/**
 * Pure reducer for builder `proposal` events.
 *
 * The server validates every proposal before it is emitted; this function is the client's
 * application step. Keeping it pure makes "a proposal changes exactly the draft fields the model
 * asked for" testable without a model or a browser.
 */

import type { TemplateVersion } from "./types";

export interface BuilderDraftSnapshot {
  name: string;
  description: string;
  plan: TemplateVersion["plan"];
  dashboard: TemplateVersion["dashboard_schema"];
  flowchart: TemplateVersion["flowchart_view"];
  selectedConnectors: string[];
}

export interface ProposalResult {
  snapshot: BuilderDraftSnapshot;
  changed: boolean;
  /** Human-readable note for the transcript, when the proposal was not a draft mutation. */
  status?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Re-projects flowchart hints after a plan change: existing labels survive for steps that still
 * exist, hints for removed steps are dropped. Nodes and edges are always derived from the plan.
 */
export function reprojectFlowchart(
  plan: TemplateVersion["plan"],
  hints: TemplateVersion["flowchart_view"],
): TemplateVersion["flowchart_view"] {
  const stepIds = new Set(plan.steps.map((step) => step.step_id));
  return { nodes: hints.nodes.filter((hint) => stepIds.has(hint.step_id)) };
}

export function applyBuilderProposal(
  snapshot: BuilderDraftSnapshot,
  kind: string,
  payload: unknown,
): ProposalResult {
  if (!isRecord(payload)) {
    return { snapshot, changed: false };
  }

  switch (kind) {
    case "metadata": {
      const name = typeof payload.name === "string" ? payload.name : snapshot.name;
      const description =
        typeof payload.description === "string" ? payload.description : snapshot.description;
      return {
        snapshot: { ...snapshot, name, description },
        changed: name !== snapshot.name || description !== snapshot.description,
      };
    }
    case "plan_patch": {
      const plan = payload.plan as TemplateVersion["plan"] | undefined;
      if (!plan || !Array.isArray(plan.steps)) return { snapshot, changed: false };
      return {
        snapshot: {
          ...snapshot,
          plan,
          flowchart: reprojectFlowchart(plan, snapshot.flowchart),
        },
        changed: true,
      };
    }
    case "dashboard": {
      const dashboard = payload.dashboard_schema as TemplateVersion["dashboard_schema"] | undefined;
      if (!dashboard || !Array.isArray(dashboard.nodes)) return { snapshot, changed: false };
      return { snapshot: { ...snapshot, dashboard }, changed: true };
    }
    case "connector": {
      const entryId = typeof payload.entry_id === "string" ? payload.entry_id.trim() : "";
      if (!entryId || snapshot.selectedConnectors.includes(entryId)) {
        return { snapshot, changed: false };
      }
      return {
        snapshot: {
          ...snapshot,
          selectedConnectors: [...snapshot.selectedConnectors, entryId],
        },
        changed: true,
      };
    }
    case "submit_proposal": {
      // The validated draft is the whole artifact; apply it wholesale.
      const draft = payload as Record<string, unknown>;
      const plan = draft.plan as TemplateVersion["plan"] | undefined;
      const dashboard = draft.dashboard_schema as TemplateVersion["dashboard_schema"] | undefined;
      const flowchart = draft.flowchart_view as TemplateVersion["flowchart_view"] | undefined;
      if (!plan || !dashboard) return { snapshot, changed: false };
      return {
        snapshot: {
          name: typeof draft.name === "string" ? draft.name : snapshot.name,
          description:
            typeof draft.description === "string" ? draft.description : snapshot.description,
          plan,
          dashboard,
          flowchart: flowchart ?? snapshot.flowchart,
          selectedConnectors: Array.isArray(draft.connectors)
            ? draft.connectors.filter((entry): entry is string => typeof entry === "string")
            : snapshot.selectedConnectors,
        },
        changed: true,
      };
    }
    case "catalog_request":
      return {
        snapshot,
        changed: false,
        status:
          typeof payload.display_name === "string"
            ? `Requested ${payload.display_name} — we'll notify you when it is available.`
            : undefined,
      };
    case "sub_template":
      return { snapshot, changed: false, status: "Sub-template proposed." };
    default:
      return { snapshot, changed: false };
  }
}
